import { FormEvent, useEffect, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './Delete.module.css';

import { useAppDispatch, useAppSelector } from '../../../../hooks/useRedux';
import {
  selectLoginType,
  selectUserEmail,
  selectUserId,
} from '../../../../features/authSlice';

import {
  useValidatePwdMutation,
  useDeleteUserMutation,
} from '../../../../services/settings';

import { Link, useLocation } from 'react-router-dom';

import {
  CANCEL,
  CANCEL_MEMBERSHIP_DATE_DESC,
  CANCEL_MEMBERSHIP,
  CANCEL_REASON,
  COMPLETE,
  CONTENT,
  ENTER,
  ENTER_PASSWORD_MESSAGE,
  ETC,
  IDENTIFICATION,
  INPUT_100_CHARS,
  MATCH_PWD,
  MEMBER,
  PASSWORD,
  WITHDRAWAL,
  WITHDRAWAL_AGREEMENT,
  WITHDRAWAL_REASON,
  WITHDRAWAL_WARNING,
  FILE_DELETION_DESC,
  STUDENT_STATUS_CHANGE_DESC,
} from '../../../../constants';
import Complete from '../../../Login/Complete/Complete';

import { limitMaxLen } from '../../../../utils/util';
import { CancelCd, CancelCdList } from '../../../../types/membership';
import { useCancelMembershipMutation } from '../../../../services/membership';
import { formatDate } from '../../../../utils/date';
import { AccountDeleteCodeList } from '../../../../types/settings';

interface Delete {
  data: {
    deleteCdList?: AccountDeleteCodeList;
    cancelCdList?: CancelCdList;
    endDate?: string;
  };
}

export default function Delete({ data }: Delete) {
  const cx = classNames.bind(styles);
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const loginType = useAppSelector(selectLoginType);
  const email = useAppSelector(selectUserEmail);
  const userId = useAppSelector(selectUserId) as number;
  const isMembership = pathname.includes('membership');
  const dataCdList = data[isMembership ? 'cancelCdList' : 'deleteCdList'] ?? [];

  const [validatePwd] = useValidatePwdMutation();
  const [deleteUser] = useDeleteUserMutation();
  const [CancelMembership] = useCancelMembershipMutation();

  const [msg, setMsg] = useState('');
  const [hasCheckedReason, setHasCheckedReason] = useState(false);
  const [isValidPwd, setIsValidPwd] = useState(false);
  const [isAgree, setIsAgree] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [account, setAccount] = useState('');
  const [isShowTextarea, setIsShowTextarea] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);
  const [cdList, setCdList] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [cancelResult, setCancelResult] = useState<any>();

  const listRef = useRef<HTMLUListElement>(null);

  const checkPwd = async ({ target }: React.FocusEvent<HTMLInputElement>) => {
    const pwd = target.value;
    try {
      const pwdRes: any = await validatePwd(pwd).unwrap();
      setMsg(pwdRes.message);
      setIsValidPwd(pwdRes.message === 'OK');
    } catch (error) {
      console.log('Failed to Validate Password', error);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isMembership) {
      try {
        const res = await CancelMembership({ cdList, reason });
        console.log('res : ', res);
        setCancelResult(res);
        setIsComplete(true);
      } catch (error) {
        console.error('Failed to Cancel Membership', error);
      }
    } else {
      try {
        await deleteUser({ userId, cdList, reason }).unwrap();
        setIsComplete(true);
      } catch (error) {
        console.log('Failed to delete account', error);
      }
    }
  };

  useEffect(() => {
    loginType === 'naver' && setIsValidPwd(true);
    email && setAccount(email);
  }, []);

  useEffect(() => {
    setHasCheckedReason(
      !!listRef?.current?.querySelectorAll('input:checked').length
    );
  }, [hasChecked]);

  return (
    <>
      <h2>{isMembership ? CANCEL_MEMBERSHIP : MEMBER + ' ' + WITHDRAWAL}</h2>
      {isComplete ? (
        <Complete
          name={isMembership ? 'cancel-membership' : 'withdrawal'}
          account={account}
          res={cancelResult}
        />
      ) : (
        <form onSubmit={handleSubmit}>
          <div className={cx('st_signup')}>
            <div className={cx('assort_area')}>
              <h3>{isMembership ? CANCEL_REASON : WITHDRAWAL_REASON}</h3>
              <ul ref={listRef}>
                {dataCdList.map(({ cd, nm }: CancelCd) => (
                  <li key={cd}>
                    <label>
                      <input
                        type="checkbox"
                        name="accessQuit"
                        className={cx('blind')}
                        value={cd}
                        onChange={({ target }) => {
                          setHasChecked(!hasChecked);
                          if (target.checked) {
                            setCdList([...cdList, cd]);
                          } else {
                            setCdList(cdList.filter((code) => code !== cd));
                          }
                          if (cd === 'U0799') {
                            setIsShowTextarea(target.checked);
                            setReason('');
                          }
                        }}
                      />
                      <span className={cx('clabel')}>{nm}</span>
                    </label>
                    {cd === 'U0799' && isShowTextarea && (
                      <div className={cx('input_box')}>
                        <span className={cx('blind')}>
                          {ETC} {CONTENT}
                        </span>
                        <textarea
                          placeholder={INPUT_100_CHARS}
                          onChange={({ target }) => {
                            target.value = limitMaxLen(target.value, 100);
                            setReason(target.value);
                          }}
                        ></textarea>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div className={cx('input_area', 'quit_area')}>
              {loginType !== 'naver' && (
                <>
                  <h3>{IDENTIFICATION}</h3>
                  <div
                    className={cx(
                      'input_box',
                      isValidPwd ? 'input--done' : 'input--error'
                    )}
                  >
                    <span className={cx('blind')}>
                      {PASSWORD} {ENTER}
                    </span>
                    <input
                      type="password"
                      title={`${PASSWORD} ${ENTER}`}
                      placeholder={ENTER_PASSWORD_MESSAGE}
                      autoCapitalize="current-password"
                      onBlurCapture={checkPwd}
                    />
                    {
                      <span className={cx('msg')}>
                        {isValidPwd ? MATCH_PWD : msg}
                      </span>
                    }
                  </div>
                </>
              )}

              {isMembership ? (
                <>
                  <div className={cx('guide_text')}>
                    <p className={cx('desc')}>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: CANCEL_MEMBERSHIP_DATE_DESC.replace(
                            '{date}',
                            formatDate(data.endDate as string, 5)
                          ),
                        }}
                      ></span>
                    </p>
                    <p className={cx('desc')}>
                      <span
                        dangerouslySetInnerHTML={{ __html: FILE_DELETION_DESC }}
                      ></span>
                    </p>
                    <p className={cx('desc')}>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: STUDENT_STATUS_CHANGE_DESC,
                        }}
                      ></span>
                    </p>
                  </div>
                  <div className={cx('check_wrap')}>
                    <label>
                      <input
                        type="checkbox"
                        name="accessDone"
                        className={cx('blind')}
                        onChange={() => setIsAgree(!isAgree)}
                      />
                      <span className={cx('clabel')}>
                        {WITHDRAWAL_AGREEMENT}
                      </span>
                    </label>
                  </div>
                </>
              ) : (
                <div className={cx('done_box')}>
                  <div className={cx('msg')}>
                    <span>{WITHDRAWAL_WARNING}</span>
                  </div>
                  <label>
                    <input
                      type="checkbox"
                      name="accessDone"
                      className={cx('blind')}
                      onChange={() => setIsAgree(!isAgree)}
                    />
                    <span className={cx('clabel')}>{WITHDRAWAL_AGREEMENT}</span>
                  </label>
                </div>
              )}
            </div>

            <div className={cx('btn_wrap', 'bottom', 'cell')}>
              <Link
                to={isMembership ? '../membership' : '../../settings'}
                type="button"
                className={cx('btn', 'b_t', 'dull', 's44')}
              >
                <span>{CANCEL}</span>
              </Link>
              <button
                type="submit"
                className={cx('btn', 'b_t', 's44', {
                  'is--disabled':
                    !isAgree ||
                    !isValidPwd ||
                    (isMembership && !hasCheckedReason),
                })}
                disabled={
                  !isAgree || !isValidPwd || (isMembership && !hasCheckedReason)
                }
              >
                <span>{COMPLETE}</span>
              </button>
            </div>
          </div>
        </form>
      )}
    </>
  );
}
